import { useEffect, useState } from "react";
import client from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { Coins, Info, AlertTriangle } from "lucide-react";

export function EnvironmentPicker({ allowed, value, onChange }) {
  const toggle = (e) => onChange(value.includes(e) ? value.filter((x) => x !== e) : [...value, e]);
  return (
    <div>
      <Label className="text-xs uppercase tracking-widest text-slate-500">Environments</Label>
      <div className="mt-3 flex flex-wrap gap-2" data-testid="env-picker">
        {allowed.map((e) => (
          <button
            key={e}
            type="button"
            onClick={() => toggle(e)}
            data-testid={`env-${e}`}
            className={cn(
              "rounded-full border px-4 py-1.5 font-mono-tight text-xs uppercase tracking-widest transition-colors",
              value.includes(e) ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 text-slate-600 hover:border-slate-400",
            )}
          >
            {e}
          </button>
        ))}
      </div>
      <p className="mt-2 font-mono-tight text-[11px] text-slate-500">
        {value.length > 1 ? `One stack per environment under environments/<env>/ with its own state key.` : "Pick several environments to scaffold dev/staging/prod at once."}
      </p>
    </div>
  );
}

export function EnvParamsEditor({ resources, catalog, envs, overrides, onChange }) {
  const selected = catalog.filter((r) => resources.includes(r.key) && Object.keys(r.defaults || {}).length);
  if (!selected.length || !envs.length) return null;
  const set = (env, key, param, v) => {
    const next = { ...overrides, [env]: { ...(overrides[env] || {}), [key]: { ...((overrides[env] || {})[key] || {}), [param]: v } } };
    if (v === "" || v === next[env][key][param] && v === (catalog.find((r) => r.key === key)?.defaults?.[param] ?? "")) {
      delete next[env][key][param];
      if (!Object.keys(next[env][key]).length) delete next[env][key];
    }
    onChange(next);
  };
  return (
    <div className="mt-6 space-y-4" data-testid="env-params">
      <div>
        <h3 className="font-display text-sm font-semibold text-slate-900">Parameters per environment</h3>
        <p className="mt-1 text-xs text-slate-500">Catalog defaults apply unless you override a value for a given environment (e.g. bigger SKU in prod).</p>
      </div>
      {selected.map((r) => (
        <div key={r.key} className="overflow-hidden rounded-md border border-slate-200" data-testid={`env-params-${r.key}`}>
          <div className="flex items-center justify-between bg-slate-50 px-4 py-2">
            <span className="font-display text-sm font-semibold">{r.name}</span>
            <span className="font-mono-tight text-[10px] uppercase tracking-widest text-slate-500">{r.key}</span>
          </div>
          <table className="w-full text-xs">
            <thead>
              <tr className="border-t border-slate-100 text-left font-mono-tight text-[10px] uppercase tracking-widest text-slate-500">
                <th className="px-4 py-2 font-medium">parameter</th>
                {envs.map((e) => <th key={e} className="px-4 py-2 font-medium">{e}</th>)}
              </tr>
            </thead>
            <tbody>
              {Object.entries(r.defaults).map(([param, def]) => (
                <tr key={param} className="border-t border-slate-100">
                  <td className="px-4 py-2 font-mono-tight text-slate-700">{param}<span className="ml-2 text-slate-400">= {String(def)}</span></td>
                  {envs.map((e) => {
                    const v = overrides[e]?.[r.key]?.[param];
                    return (
                      <td key={e} className="px-4 py-1.5">
                        <Input
                          data-testid={`param-${e}-${r.key}-${param}`}
                          value={v ?? ""}
                          placeholder={String(def)}
                          onChange={(ev) => set(e, r.key, param, ev.target.value)}
                          className={cn("h-8 border-slate-200 font-mono-tight text-xs", v !== undefined && "border-slate-900 bg-slate-50")}
                        />
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}

export function CostEstimate({ cloud, region, envs, resources, catalog, overrides, budgets = {} }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      const out = {};
      for (const env of envs) {
        const body = {
          cloud, region,
          resources: resources.map((key) => ({ key, params: { ...(catalog.find((r) => r.key === key)?.defaults || {}), ...((overrides[env] || {})[key] || {}) } })),
        };
        try {
          out[env] = (await client.post("/pricing/estimate", body)).data;
        } catch {
          out[env] = null;
        }
      }
      if (!cancelled) setData(out);
    };
    run();
    return () => { cancelled = true; };
  }, [cloud, region, envs, resources, catalog, overrides]);

  if (!envs.length) return null;
  const total = data ? Object.values(data).reduce((s, d) => s + (d?.monthly_usd || 0), 0) : null;
  const over = data ? envs.filter((env) => (budgets[env] || 0) > 0 && (data[env]?.monthly_usd || 0) > budgets[env]) : [];
  return (
    <Card className={cn("rounded-md border bg-white p-6 shadow-none", over.length ? "border-rose-300" : "border-slate-200")} data-testid="cost-estimate">
      <div className="mb-4 flex items-center justify-between">
        <h3 className="flex items-center gap-2 font-display text-base font-semibold"><Coins className="h-4 w-4 text-slate-400" /> Estimated monthly cost</h3>
        <span className="font-display text-lg font-semibold" data-testid="cost-total">{total === null ? "…" : `$${total.toFixed(2)}`}<span className="ml-1 font-mono-tight text-[10px] uppercase tracking-widest text-slate-400">/ mo</span></span>
      </div>
      {over.length > 0 && (
        <div className="mb-4 flex items-start gap-2 rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800" data-testid="budget-alert">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            <span className="font-medium">Over budget.</span> {over.map((env) => `${env}: $${data[env].monthly_usd.toFixed(2)} vs $${Number(budgets[env]).toFixed(0)} budget`).join(" · ")}. You can still generate, but the project will be flagged.
          </div>
        </div>
      )}
      <div className={cn("grid gap-3", envs.length > 1 ? "md:grid-cols-3" : "grid-cols-1")}>
        {envs.map((env) => {
          const d = data?.[env];
          const isOver = over.includes(env);
          return (
            <div key={env} className={cn("rounded-md border p-4", isOver ? "border-rose-200 bg-rose-50/40" : "border-slate-100 bg-slate-50/60")} data-testid={`cost-env-${env}`}>
              <div className="flex items-center justify-between">
                <span className="font-mono-tight text-[10px] uppercase tracking-widest text-slate-500">{env}{budgets[env] > 0 && <span className="ml-2 text-slate-400">budget ${Number(budgets[env]).toFixed(0)}</span>}</span>
                <span className={cn("font-display text-base font-semibold", isOver && "text-rose-700")} data-testid={`cost-env-total-${env}`}>{d ? `$${d.monthly_usd.toFixed(2)}` : "…"}</span>
              </div>
              <ul className="mt-3 space-y-1.5">
                {d?.items.map((i) => (
                  <li key={i.key} className="text-xs">
                    <div className="flex justify-between gap-2 text-slate-800"><span className="truncate font-mono-tight">{i.key}</span><span>${i.monthly_usd.toFixed(2)}</span></div>
                    <div className="truncate text-[10px] text-slate-500">{i.basis}</div>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>
      {data && (
        <p className="mt-4 flex items-start gap-1.5 font-mono-tight text-[10px] text-slate-500">
          <Info className="mt-0.5 h-3 w-3 shrink-0" />
          Sources: {[...new Set(Object.values(data).flatMap((d) => d?.sources || []))].join(", ")}. Indicative on-demand list prices with fixed usage assumptions.
        </p>
      )}
    </Card>
  );
}
